import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

interface UseRefreshableDataOptions {
	/**
	 * Unique key for localStorage to track cooldown across sessions
	 */
	storageKey: string;
	/**
	 * Cooldown duration in milliseconds (default: 1 hour)
	 */
	cooldownMs?: number;
	/**
	 * React Query key(s) to invalidate on refresh
	 */
	queryKey: string | string[];
	/**
	 * Optional async function to bust backend cache before invalidating queries
	 */
	onCacheBust?: () => Promise<unknown>;
}

interface UseRefreshableDataReturn {
	/**
	 * Whether a refresh can be triggered (not on cooldown and not currently refreshing)
	 */
	canRefresh: boolean;
	/**
	 * Whether a refresh is currently in progress
	 */
	isRefreshing: boolean;
	/**
	 * Timestamp of when data was last checked/refreshed
	 */
	lastChecked: Date | null;
	/**
	 * When the cooldown ends (null if no cooldown active)
	 */
	cooldownEndsAt: Date | null;
	/**
	 * Trigger a refresh - busts cache, invalidates queries, resets cooldown
	 */
	refresh: () => Promise<void>;
}

/**
 * Hook for managing refreshable cached data with rate limiting.
 *
 * Provides cooldown-based refresh functionality that:
 * - Persists cooldown state in localStorage
 * - Optionally busts backend cache before invalidating React Query
 * - Tracks loading and last-checked states
 *
 * @example
 * ```tsx
 * const { canRefresh, isRefreshing, lastChecked, refresh } = useRefreshableData({
 *   storageKey: "dashboard_last_refresh",
 *   queryKey: "dashboard",
 *   onCacheBust: () => orpc.dashboard.refreshCache.call({}),
 * });
 * ```
 */
export function useRefreshableData({
	storageKey,
	cooldownMs = 60 * 60 * 1000, // 1 hour default
	queryKey,
	onCacheBust,
}: UseRefreshableDataOptions): UseRefreshableDataReturn {
	const queryClient = useQueryClient();
	const [lastChecked, setLastChecked] = useState<Date | null>(null);
	const [canRefresh, setCanRefresh] = useState(false);
	const [cooldownEndsAt, setCooldownEndsAt] = useState<Date | null>(null);
	const [isRefreshing, setIsRefreshing] = useState(false);

	const checkCooldown = useCallback(() => {
		const lastRefresh = localStorage.getItem(storageKey);
		if (!lastRefresh) {
			setCanRefresh(true);
			setCooldownEndsAt(null);
			return;
		}
		const lastRefreshTime = parseInt(lastRefresh, 10);
		const elapsed = Date.now() - lastRefreshTime;
		const canRefreshNow = elapsed >= cooldownMs;
		setCanRefresh(canRefreshNow);
		setCooldownEndsAt(
			canRefreshNow ? null : new Date(lastRefreshTime + cooldownMs),
		);
	}, [storageKey, cooldownMs]);

	const refresh = useCallback(async () => {
		if (!canRefresh || isRefreshing) return;

		localStorage.setItem(storageKey, Date.now().toString());
		setCanRefresh(false);
		setIsRefreshing(true);

		try {
			// Bust backend cache if handler provided
			if (onCacheBust) {
				await onCacheBust();
			}
			// Invalidate React Query cache
			const keys = Array.isArray(queryKey) ? queryKey : [queryKey];
			await Promise.all(
				keys.map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
			);
			setLastChecked(new Date());
		} finally {
			setIsRefreshing(false);
		}
	}, [
		canRefresh,
		isRefreshing,
		storageKey,
		queryKey,
		queryClient,
		onCacheBust,
	]);

	useEffect(() => {
		setLastChecked(new Date());
		checkCooldown();
		const interval = setInterval(checkCooldown, 60_000); // Check every minute
		return () => clearInterval(interval);
	}, [checkCooldown]);

	return {
		canRefresh,
		isRefreshing,
		lastChecked,
		cooldownEndsAt,
		refresh,
	};
}
